<template>
  <v-container>
    <h1 class="primary--text font-weight-bold pb-4">
      {{ isEdit ? $t("plans.editSubscription") : $t("plans.addSubscription") }}
    </h1>
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <v-form ref="form">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-select
                    :items="plans"
                    item-text="planName"
                    item-value="_id"
                    v-model="selectedPlan"
                    :label="$t('plans.select')"
                    :error-messages="errors"
                    outlined
                    dense
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="6">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-menu
                    ref="startMenu"
                    v-model="startMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="startDateFormatted"
                        :label="$t('plans.startDate')"
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        :error-messages="errors"
                      />
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      :max="endDate"
                      @input="startMenu = false"
                    />
                  </v-menu>
                </ValidationProvider>
              </v-col>

              <v-col cols="6">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-menu
                    ref="endMenu"
                    v-model="endMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="endDateFormatted"
                        :label="$t('plans.endDate')"
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        :error-messages="errors"
                      />
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      :min="startDate"
                      @input="endMenu = false"
                    />
                  </v-menu>
                </ValidationProvider>
              </v-col>

              <v-col cols="3">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model.number="numberOfProperties"
                    :label="$t('plans.numberOfProperties')"
                    :error-messages="errors"
                    outlined
                    dense
                    type="number"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="3">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model.number="numberOfUnits"
                    :label="$t('plans.numberOfUnits')"
                    :error-messages="errors"
                    outlined
                    dense
                    type="number"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="3">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model.number="numberOfUsers"
                    :label="$t('plans.numberOfUsers')"
                    :error-messages="errors"
                    outlined
                    dense
                    type="number"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="6">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-select
                    :error-messages="errors"
                    v-model="paymentType"
                    :items="paymentTypes"
                    dense
                    @change="paymentTypeChange"
                    outlined
                    :placeholder="$t('rentType.paymentMethod')"
                  ></v-select>
                </ValidationProvider>
              </v-col>
              <v-col cols="6">
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <v-text-field
                    v-model.number="amount"
                    :label="$t('core.amount')"
                    :readonly="paymentType === 'FREE'"
                    :error-messages="errors"
                    outlined
                    dense
                    type="number"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row class="pt-5" justify="space-around">
          <v-col cols="4">
            <v-btn
              width="100%"
              class="white--text"
              color="primary"
              @click="closeForm"
            >
              {{ $t("common.cancel") }}
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
              width="100%"
              :loading="loading"
              :disabled="invalid"
              @click="saveSubscription"
              class="white--text"
              color="primary"
            >
              {{ isEdit ? $t("common.update") : $t("common.save") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
export default {
  props: {
    isEdit: Boolean,
    subscriptionId: String,
    landlordId: String
  },
  data() {
    return {
      selectedPlan: null,
      startDate: new Date(),
      endDate: null,
      startMenu: false,
      endMenu: false,
      plans: [],
      loading: false,
      amount: null,
      skipEndDateUpdate: false,
      paymentTypes: ["ONLINE", "OFFLINE", "FREE"],
      paymentType: "",
      numberOfProperties: 0,
      numberOfUnits: 0,
      numberOfUsers: 0
    };
  },
  computed: {
    startDateFormatted() {
      return this.startDate
        ? new Date(this.startDate).toLocaleDateString()
        : "";
    },
    endDateFormatted() {
      return this.endDate ? new Date(this.endDate).toLocaleDateString() : "";
    }
  },
  watch: {
    // Watch for plan changes
    selectedPlan(newVal) {
      const plan = this.plans.find(p => p._id === newVal);
      this.amount = plan ? plan.price : null;
      this.numberOfProperties = plan ? plan.numberOfProperties : 0;
      this.numberOfUnits = plan ? plan.numberOfUnits : 0;
      this.numberOfUsers = plan ? plan.numberOfUsers : 0;
      this.updateEndDate();
    },
    // Watch for start date changes
    startDate() {
      this.updateEndDate();
    }
  },
  apollo: {
    getValidityPlans: {
      query: require("@/plans/api/queries/get_all_landlord_plans.graphql"),
      variables() {
        return {
          input: {
            limit: 50,
            skip: 0
          },
          filter: {
            searchKey: "",
            sortBy: "",
            sortDesc: false
          }
        };
      },
      result({ data }) {
        this.plans = data.getAllLandlordValidityPlan.landlordValidityPlans.filter(
          plan => plan.isActive === true
        );
      }
    },
    subscriptionDetails: {
      query: require("@/subscriptions/api/queries/get_subscription_by_id.graphql"),
      variables() {
        return {
          id: this.subscriptionId
        };
      },
      skip() {
        return !this.isEdit;
      },
      result({ data }) {
        const subscription = data.getLandlordSubscriptionById;
        if (subscription) {
          // Prevent automatic end date update during initialization
          this.skipEndDateUpdate = true;

          this.selectedPlan = subscription.landlordValidityPlan._id;
          this.startDate = this.formatDate(subscription.startDate);
          this.endDate = this.formatDate(subscription.endDate);
          this.amount = subscription.amount;

          // Re-enable updates after initialization
          this.$nextTick(() => {
            this.skipEndDateUpdate = false;
          });
        }
      }
    }
  },
  methods: {
    paymentTypeChange() {
      if (this.paymentType == "FREE") {
        this.amount = 0;
      } else {
        const plan = this.plans.find(p => p._id === this.selectedPlan);
        this.amount = plan ? plan.price : null;
      }
    },
    updateEndDate() {
      if (this.skipEndDateUpdate || !this.selectedPlan || !this.startDate)
        return;
      const start = new Date(this.startDate);
      const endDate = new Date(start);
      endDate.setDate(start.getDate() + parseInt(31));
      this.endDate = endDate.toISOString().split("T")[0];
    },
    formatDate(timestamp) {
      if (!timestamp) return null;
      const date = new Date(parseInt(timestamp));
      return date.toISOString().split("T")[0];
    },
    async saveSubscription() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;

      this.loading = true;

      try {
        const variables = {
          input: {
            landlordId: this.landlordId,
            landlordValidityPlanId: this.selectedPlan,
            startDate: this.startDate,
            endDate: this.endDate,
            amount: this.amount,
            paymentType: this.paymentType,
            numberOfProperties: this.numberOfProperties,
            numberOfUsers: this.numberOfUsers,
            numberOfUnits: this.numberOfUnits
          }
        };
        if (this.isEdit) {
          await this.$apollo.mutate({
            mutation: require("@/subscriptions/api/mutations/update_landlord_subscription.graphql"),
            variables: { id: this.subscriptionId, ...variables }
          });
        } else {
          await this.$apollo.mutate({
            mutation: require("@/subscriptions/api/mutations/create_landlord_subscription.graphql"),
            variables
          });
        }

        this.$root.$emit("snackbar", {
          text: this.$t("common.saveSuccess"),
          color: "success"
        });
        this.$emit("refetch");
        this.closeForm();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    closeForm() {
      this.$refs.form.reset();
      this.$store.commit("SHOW_RIGHTSIDE_DRAWER", false);
    }
  }
};
</script>
